import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, MapPin, Phone, ArrowRight, Shield, Award } from 'lucide-react';

const SERVICES = [
  { name: 'Pipeline Repair & Rehabilitation', path: '/services/pipeline-repair', desc: 'Composite wraps, pipe lining, leak sealing — no hot work, no shutdown.' },
  { name: 'Tank Lining & Repair', path: '/services/tank-repair', desc: 'Interior linings, structural repair, and corrosion protection for all tank types.' },
  { name: 'Structural Concrete Repair', path: '/services/structural-repair', desc: 'Spall repair, carbon fiber strengthening, crack injection, and waterproofing.' },
  { name: 'Protective Coatings', path: '/services/protective-coatings', desc: 'Industrial coating systems for steel, concrete, and specialty substrates.' },
  { name: 'Equipment Repair', path: '/services/equipment-repair', desc: 'Pumps, valves, heat exchangers, and rotating equipment restoration.' },
  { name: 'Industrial Flooring', path: '/services/floor-repair', desc: 'Epoxy, polyurethane, and MMA floor coating systems for industrial facilities.' },
  { name: 'HVAC & Cooling Systems', path: '/services/hvac-repair', desc: 'Cooling tower repair, chiller tube coating, and HVAC system rehabilitation.' },
  { name: 'Roof Repair & Coatings', path: '/services/roof-repair', desc: 'Industrial roof restoration, waterproofing, and reflective coating systems.' },
  { name: 'Asset Life Extension', path: '/services/asset-life-extension', desc: 'Comprehensive programs to extend the life of aging industrial infrastructure.' },
];

const CREDENTIALS = [
  'CSLB License No. 1066525 | Class A & C-33',
  'AMPP/NACE Certified',
  'DIR No. 2000002659',
  'DBIDS Gov Contractor',
  'TWIC Credentialed',
  'DUNS No. 080247052',
  'WBENC No. WBE2600603',
];

const ServiceAreaPage = ({ location }) => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { name, heroDesc, region, cities, industries, metaTitle, metaDesc, mapQuery } = location;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={metaTitle} description={metaDesc} path={`/service-areas/${location.slug}`} keywords={[`general contractor ${name}`, `industrial coatings ${name}`, `pipeline repair ${name}`, `structural repair ${name}`]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-blue-300 text-sm font-semibold uppercase tracking-wider mb-4">
            <MapPin size={14} /> Service Area
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            General Contractor in {name}
          </h1>
          <p className="text-lg text-blue-100/80 max-w-3xl mb-8">{heroDesc}</p>
          <div className="flex gap-3">
            <Button onClick={() => window.location.href = `mailto:info@bcs-us.com?subject=${encodeURIComponent('Service Inquiry — ' + region)}&body=${encodeURIComponent('Hi BCS team,\n\nI\'d like to get in touch about services in ' + region + '.\n\nThanks!')}`} className="bg-white text-blue-900 hover:bg-blue-50 px-6 py-6 text-base">Get In Touch</Button>
            <Button onClick={() => window.open('tel:9493719948')} variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-6 text-base">
              <Phone size={16} className="mr-2" /> (949) 371-9948
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">Our Services in {name}</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">BCS provides comprehensive general contracting services for capital improvements, structural rehabilitation, and asset protection across {region}.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((svc, i) => (
              <Card key={i} className="group cursor-pointer hover:shadow-md transition-all border-gray-200 hover:border-blue-300"
                onClick={() => navigate(svc.path)}>
                <CardContent className="pt-5 pb-5">
                  <h3 className="font-semibold text-slate-900 mb-1.5 group-hover:text-blue-800 transition-colors flex items-center justify-between">
                    {svc.name} <ArrowRight size={14} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                  </h3>
                  <p className="text-sm text-gray-500">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why BCS */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why {name} Facilities Choose BCS</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <Shield className="mx-auto mb-3 text-blue-900" size={32} />
              <h3 className="font-semibold text-lg mb-2">AMPP/NACE Certified</h3>
              <p className="text-sm text-gray-500">Our teams hold the industry's highest certifications for coating inspection and application quality.</p>
            </div>
            <div className="text-center p-6">
              <Award className="mx-auto mb-3 text-blue-900" size={32} />
              <h3 className="font-semibold text-lg mb-2">Licensed & Insured</h3>
              <p className="text-sm text-gray-500">CSLB License No. 1066525 (Class A &amp; C-33), DIR registered, DBIDS approved. Full insurance coverage for any project size.</p>
            </div>
            <div className="text-center p-6">
              <MapPin className="mx-auto mb-3 text-blue-900" size={32} />
              <h3 className="font-semibold text-lg mb-2">Local {name} Presence</h3>
              <p className="text-sm text-gray-500">Fast mobilization across {region} with teams ready to respond to emergency and planned maintenance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industries We Serve in {name}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {industries.map((ind, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-blue-50/50 rounded-lg">
                <CheckCircle size={14} className="text-blue-600 flex-shrink-0" />
                <span className="text-sm text-slate-700">{ind}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Served */}
      {cities && cities.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Cities We Serve in {name}</h2>
            <div className="flex flex-wrap gap-2 justify-center">
              {cities.map((city, i) => (
                <span key={i} className="px-3 py-1.5 bg-white border rounded-full text-sm text-gray-600">{city}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Credentials Bar */}
      <section className="py-8 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-4 justify-center">
          {CREDENTIALS.map((c, i) => (
            <span key={i} className="text-xs text-blue-200/80 font-medium">{c}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Ready to Start Your {name} Project?</h2>
          <p className="text-gray-500 mb-8">Contact BCS for a free assessment and proposal. We serve all of {region} with fast mobilization.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.href = `mailto:info@bcs-us.com?subject=${encodeURIComponent('Quote Request — ' + region)}&body=${encodeURIComponent('Hi BCS team,\n\nI\'d like to request a quote for a project in ' + region + '.\n\n(Please share project details here.)\n\nThanks!')}`} className="bg-blue-900 hover:bg-blue-800 px-8 py-6 text-base">Request a Quote</Button>
            <Button onClick={() => window.open('tel:9493719948')} variant="outline" className="px-8 py-6 text-base">
              <Phone size={16} className="mr-2" /> Call (949) 371-9948
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreaPage;
